import { useRef, useState, type ReactNode } from 'react';
import { ActionButton, ActionTextarea, Chip, OptionButton } from '../../../parts/index.js';
import { ApiError } from '../../../api.js';
import { NotSaved, Refused } from '../states/States.js';
import { control, flow, stack, usePhone } from '../home/layout.js';
export function Change({ label, sentence, children, ready = true, onSave, onSaved, onCancel, onConflict, drawing = false }: {
  drawing?: boolean; label: string; sentence?: string; children?: ReactNode; ready?: boolean;
  onSave: (reason: string) => Promise<unknown>; onSaved: () => void; onCancel: () => void; onConflict: () => void;
}) {
  const [reason, setReason] = useState(''); const [busy, setBusy] = useState(false); const [error, setError] = useState<unknown>();
  const inflight = useRef(false); const phone = usePhone();
  const style = { ...control, minHeight: phone ? 'var(--tap)' : 'var(--opt-h)', ...(phone ? { width: '100%' } : {}) };
  async function save() {
    if (inflight.current || !ready || !reason.trim()) return;
    inflight.current = true; setBusy(true); setError(undefined);
    try { await onSave(reason.trim()); onSaved(); }
    catch (e) {
      // A bare 404 means the server no longer offers this change (pause switched off): close and reload, which hides it.
      if (e instanceof ApiError && e.status === 404 && e.message === 'not found') { onCancel(); onConflict(); return; }
      setError(e); if (e instanceof ApiError && e.status === 409) onConflict();
    }
    finally { inflight.current = false; setBusy(false); }
  }
  if (drawing) return <div className="confirm" aria-busy={busy}>
    <span className="chip">Not saved yet</span>{sentence && <p className="q">{sentence}</p>}{children}
    <label className="field">Reason<ActionTextarea required disabled={busy} value={reason} onChange={e => setReason(e.target.value)} /></label>
    <div className="opts"><ActionButton className="opt lead" disabled={busy || !ready || !reason.trim()} onClick={() => void save()}>{label}</ActionButton>
      {!reason.trim() && <span className="note">Give a reason first</span>}
      <ActionButton className="opt" disabled={busy} onClick={onCancel}>Cancel</ActionButton></div>
    {error != null && (error instanceof ApiError && [403, 404, 409, 422].includes(error.status)
      ? <Refused words={error.message} /> : <NotSaved reason={error instanceof Error ? error.message : String(error)} onRetry={() => void save()} />)}
  </div>;
  return <div style={{ ...stack, border: 'var(--r-sm) solid var(--ink)', padding: 'var(--pad-y) var(--pad-x)', background: 'var(--surface)' }}>
    <Chip style={{ background: 'var(--surface-2)' }}>Not saved yet</Chip>
    {sentence && <p>{sentence}</p>}{children}
    <label style={stack}>Reason<ActionTextarea required disabled={busy} style={{ ...style, width: '100%', boxSizing: 'border-box' }} value={reason} onChange={e => setReason(e.target.value)} /></label>
    <div style={flow}>
      <OptionButton variant="lead" style={style} disabled={busy || !ready || !reason.trim()} onClick={() => void save()}>{label}</OptionButton>
      {!reason.trim() && <span>Give a reason first</span>}
      <ActionButton className="dotted" style={style} disabled={busy} onClick={onCancel}>Cancel</ActionButton>
    </div>
    {error != null && (error instanceof ApiError && [403, 404, 409, 422].includes(error.status)
      ? <Refused words={error.message} /> : <NotSaved reason={error instanceof Error ? error.message : String(error)} onRetry={() => void save()} />)}
  </div>;
}
